/**
 * MAKYO Performance Utilities
 * Mobile detection and performance monitoring for shader optimization
 */

export interface DeviceCapabilities {
  isMobile: boolean
  isHighDPI: boolean
  shouldDownscale: boolean
  maxPixelRatio: number
  supportsWebGL2: boolean
  recommendFallback: boolean
}

/**
 * Detect device capabilities and recommend performance settings
 */
export function detectDeviceCapabilities(): DeviceCapabilities {
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
  const isHighDPI = window.devicePixelRatio > 2
  const pixelCount = window.innerWidth * window.innerHeight
  const isHighResolution = pixelCount > 2073600 // >1080p

  const canvas = document.createElement('canvas')
  const supportsWebGL2 = !!canvas.getContext('webgl2')

  return {
    isMobile,
    isHighDPI,
    shouldDownscale: isMobile && (isHighDPI || isHighResolution),
    maxPixelRatio: isMobile ? Math.min(window.devicePixelRatio, 1.5) : Math.min(window.devicePixelRatio, 2),
    supportsWebGL2,
    recommendFallback: isMobile && isHighResolution && isHighDPI,
  }
}

/**
 * Get optimal FBM iterations based on device
 */
export function getOptimalFBMIterations(capabilities: DeviceCapabilities): number {
  if (capabilities.recommendFallback) return 2
  if (capabilities.isMobile) return 3
  return 5
}

/**
 * Calculate optimal resolution scale
 */
export function getResolutionScale(capabilities: DeviceCapabilities): number {
  if (capabilities.recommendFallback) return 0.5
  if (capabilities.shouldDownscale) return 0.75
  return 1.0
}
